import {
  GeoCoordinates,
  LocalBusiness,
  LocationFeatureSpecification,
  OfferCatalog,
  WithContext,
} from "schema-dts";

import { DISPENSARY_PLACEHOLDER_PHOTO } from "@/constants/dispensary";
import { Dispensary } from "@/custom-types/Dispensary";
import { DispensaryReview } from "@/custom-types/Reviews";
import formatImgixUrl from "@/utils/image/formatImgixUrl";
import generateOpeningHoursSchema from "@/utils/schemas/generateOpeningHoursSchema";
import generateReviewsSchema from "@/utils/schemas/generateReviewsSchema";

import { ProductRecTrayData } from "@/components/Dispensary/ProductRecTrays/ProductRecTrays";

import generateMenuItemProductSchema from "./generateMenuItemProductSchema";

export default (
  dispensary: Dispensary,
  baseUrl: string,
  countryCode: string,
  reviews?: DispensaryReview[],
  productsForCategoryCarousels?: ProductRecTrayData[],
): WithContext<LocalBusiness> => {
  return {
    "@context": "https://schema.org",
    "@id": `${baseUrl}${dispensary.path}`,
    "@type": "LocalBusiness",
    address: {
      "@type": "PostalAddress",
      ...(dispensary.country && { addressCountry: dispensary.country }),
      ...(dispensary.city && { addressLocality: dispensary.city }),
      ...(dispensary.state && { addressRegion: dispensary.state }),
      ...(dispensary.zip && { postalCode: dispensary.zip }),
      ...(dispensary.address1 && { streetAddress: dispensary.address1 }),
    },
    ...(dispensary.roundedRating &&
      dispensary.reviewCount && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: dispensary.roundedRating,
          reviewCount: dispensary.reviewCount,
        },
      }),
    amenityFeature: generateAmenityFeatures(dispensary),
    ...(dispensary.description && { description: dispensary.description }),
    ...(dispensary.email && { email: dispensary.email }),
    ...(dispensary.locations?.length && {
      geo: generateGeoSchema(dispensary.locations[0]),
    }),
    ...(productsForCategoryCarousels?.length && {
      hasOfferCatalog: generateOfferSchemas(
        productsForCategoryCarousels,
        countryCode,
      ),
    }),
    image:
      formatImgixUrl(dispensary.coverPhotoUrl) || DISPENSARY_PLACEHOLDER_PHOTO,
    ...(dispensary.logoUrl && { logo: formatImgixUrl(dispensary.logoUrl) }),
    name: dispensary.name,
    ...(dispensary.schedules && {
      openingHoursSpecification: generateOpeningHoursSchema(
        dispensary.schedules,
      ),
    }),
    priceRange: "$$",
    ...(dispensary.phone && { telephone: dispensary.phone }),
    url: `${baseUrl}${dispensary.path}`,
    ...(reviews?.length && { reviews: generateReviewsSchema(reviews) }),
  };
};

const generateAmenityFeatures = (
  dispensary: Dispensary,
): LocationFeatureSpecification[] => [
  {
    "@type": "LocationFeatureSpecification",
    name: "ATM",
    value: !!dispensary.hasAtm,
  },
  {
    "@type": "LocationFeatureSpecification",
    name: "Veteran discount",
    value: !!dispensary.hasVeteranDiscount,
  },
  {
    "@type": "LocationFeatureSpecification",
    name: "ADA accessible",
    value: !!dispensary.isAdaAccessible,
  },
  {
    "@type": "LocationFeatureSpecification",
    name: "Accepts debit cards",
    value: !!dispensary.acceptsDebitCards,
  },
];

const generateGeoSchema = (location: {
  lat: number;
  lon: number;
}): GeoCoordinates => ({
  "@type": "GeoCoordinates",
  ...(location?.lat && { latitude: location.lat }),
  ...(location?.lon && { longitude: location.lon }),
});

const generateOfferSchemas = (
  productsForCategoryCarousels: ProductRecTrayData[],
  countryCode: string,
): OfferCatalog[] =>
  productsForCategoryCarousels.map((carousel) => ({
    "@type": "OfferCatalog",
    itemListElement: carousel.menuItems.map((product) =>
      generateMenuItemProductSchema(product, countryCode),
    ),
    name: carousel.name,
  }));
